import { useParams } from "react-router-dom";
import { listCapabilities, listStandards, listTools } from "@/api/endpoints";
import { useAsync } from "@/lib/useAsync";
import { Card } from "@/components/ui/Card";
import { Badge } from "@/components/ui/Badge";
import { Markdown } from "@/components/ui/Markdown";
import { PillButton } from "@/components/ui/PillButton";
import { FlagsSection } from "@/components/FlagsSection";
import { RadarSection } from "@/components/RadarSection";
import { NotesSection } from "@/components/NotesSection";
import { ToolLinks } from "@/components/ToolLinks";
import { TrelicaSection } from "@/components/TrelicaSection";
import type { Tool } from "@/api/types";
import { updatedLabel } from "@/lib/time";
import {
  FLAG_LABELS,
  attentionCounts,
  attentionOptions,
  catalogSearchable,
  passesAttention,
  toolOrigin,
  type AttentionFilter,
  type CatalogEntry,
} from "@/lib/toolSearch";
import { CatalogCard } from "@/components/CatalogCard";
import { FitsReminder } from "@/components/HowItFitsTogether";
import { BrowseLoading, BrowsePage } from "@/components/BrowsePage";

/** The fits-together reminder, plus a note that this tab mixes tools Guild built with ones it buys. */
function ToolsIntro() {
  return (
    <>
      <FitsReminder />
      <p className="mb-4 max-w-4xl text-sm text-text/60">
        This page lists both <span className="font-medium text-text/80">Guild-built</span> tools (internal platforms and
        libraries, marked with the Guild logo) and <span className="font-medium text-text/80">third party</span> tools
        (vendors, cloud services, open source, languages and frameworks). Click an origin to filter by it.
      </p>
    </>
  );
}

type ToolEntry = Extract<CatalogEntry, { type: "tool" }>;

/** "What tool should I use for X" — a curated guidance catalog cross-referencing the
 * standards registry rather than duplicating it (Datadog satisfies logging/tracing;
 * Cycode satisfies sca-sast-checks). See docs/production-service-decision-record.md's
 * target data model. Inventory detail lives in Trelica, joined by trelica_app_id and read
 * live in a tool's detail only (see components/TrelicaSection.tsx) — the grid never calls
 * Trelica, so browsing the catalog doesn't fan out requests. The radar's practices
 * are on the Standards tab: they're things to do, not to use.
 *
 * The shared browse layout (components/BrowsePage.tsx). Its pulldown is Needs attention:
 * everything, anything flagged (every flag but "not yet reviewed", which is on nearly every
 * imported tool and is its own choice), or one kind of flag. Each card lists its flags; a
 * tool is marked reviewed only on its detail page (`/tools/<id>`). */
export function ToolsPage() {
  const state = useAsync(() => Promise.all([listTools(), listStandards(), listCapabilities()]), []);
  const { toolId } = useParams();

  if (state.status !== "ready")
    return (
      <BrowseLoading
        title="Tools"
        intro={<ToolsIntro />}
        noun="tools"
        error={state.status === "error" ? state.message : undefined}
      />
    );

  const [{ tools, last_updated }, { standards }, { capabilities }] = state.data;
  const standardNames = new Map(standards.map((s) => [s.id, s.name]));
  const capabilityNames = new Map(capabilities.map((c) => [c.id, c.name]));
  const entries: ToolEntry[] = tools
    .map((item) => ({ type: "tool" as const, item }))
    .sort((a, b) => a.item.name.localeCompare(b.item.name));
  const counts = attentionCounts(entries);

  return (
    <BrowsePage
      title="Tools"
      meta={
        <>
          <span title={updatedLabel(last_updated).title}>{updatedLabel(last_updated).text}</span>
          {" · "}
          {tools.length} tools
        </>
      }
      intro={<ToolsIntro />}
      items={entries}
      itemKey={(e) => e.item.id}
      itemPath={(e) => `/tools/${encodeURIComponent(e.item.id)}`}
      selectedKey={toolId ?? null}
      basePath="/tools"
      searchable={(e) => catalogSearchable(e, standardNames, capabilityNames)}
      hintLabels={{ flag: FLAG_LABELS }}
      storageKey="tools"
      noun="tools"
      filter={{
        options: attentionOptions(counts),
        passes: (e, value) => passesAttention(e, value as AttentionFilter),
      }}
      card={(e) => <CatalogCard entry={e} standardNames={standardNames} />}
      row={(e) => <span className="truncate">{e.item.name}</span>}
      detail={(e, onSearch) => (
        <ToolDetail
          tool={e.item}
          standardNames={standardNames}
          capabilityNames={capabilityNames}
          onChanged={state.reload}
          onSearch={onSearch}
        />
      )}
    />
  );
}

/** Who wrote a tool's description, so a reader knows whether to trust it as Guild's view. */
const WHAT_SOURCE_LABEL: Record<Tool["what_source"], string> = {
  radar: "Description from the Tech Radar",
  trelica: "Placeholder from Trelica — not on the Tech Radar",
  seed: "Illustrative seed text",
  person: "Written in the registry",
};

/** One tool in full: anything that needs attention (with its usage evidence), what it is
 * and who wrote that, its confirmed and drafted links to
 * standards and capabilities (drafted ones confirmed or dismissed here), where its ring and status came from on the Tech Radar,
 * its live Trelica section, API documentation status, its editable notes, and its
 * lifecycle history. `onChanged` reloads the catalog after the tool is linked in Trelica. */
function ToolDetail({
  tool: t,
  standardNames,
  capabilityNames,
  onChanged,
  onSearch,
}: {
  tool: Tool;
  standardNames: Map<string, string>;
  capabilityNames: Map<string, string>;
  onChanged: () => void;
  onSearch: (field: string, value: string, toggle?: boolean) => void;
}) {
  return (
    <Card className="flex flex-col gap-3">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h3 className="font-[family-name:--font-display] text-xl">{t.name}</h3>
          <div className="mt-1 flex flex-wrap gap-1">
            {t.category && (
              <PillButton
                onClick={(toggle) => onSearch("category", t.category, toggle)}
                title={`Show every tool in ${t.category}`}
              >
                <Badge>{t.category}</Badge>
              </PillButton>
            )}
            {toolOrigin(t) && (
              <PillButton
                onClick={(toggle) => onSearch("origin", toolOrigin(t)!, toggle)}
                title={`Show every ${toolOrigin(t)} tool`}
              >
                <Badge>{toolOrigin(t)}</Badge>
              </PillButton>
            )}
          </div>
        </div>
        <PillButton onClick={(toggle) => onSearch("status", t.status, toggle)} title={`Show every ${t.status} tool`}>
          <Badge tone={t.status === "active" ? "accent" : "warn"}>{t.status}</Badge>
        </PillButton>
      </div>

      <FlagsSection type="tool" entry={t} trelicaAppId={t.trelica_app_id} onChanged={onChanged} />

      <div>
        <Markdown text={t.what} />
        <p className="mt-1 text-xs text-text/50">{WHAT_SOURCE_LABEL[t.what_source]}</p>
      </div>

      <ToolLinks tool={t} standardNames={standardNames} capabilityNames={capabilityNames} onChanged={onChanged} />

      <RadarSection tool={t} category={t.category} onSearch={onSearch} />

      {/* Keyed by tool so its catalog search, results and relinking state start fresh for each
          tool opened from the side list, rather than carrying over from the previous one. */}
      <TrelicaSection key={t.id} tool={t} onLinked={onChanged} />

      <div className="text-sm">
        <div>
          <h4 className="mb-1 text-xs font-semibold tracking-wide text-text/60 uppercase">API</h4>
          <p className="text-text/70">
            {t.api.status}
            {t.api.url && (
              <>
                {" — "}
                <a href={t.api.url} target="_blank" rel="noreferrer" className="text-accent underline">
                  {t.api.url}
                </a>
              </>
            )}
          </p>
        </div>
      </div>

      <NotesSection key={t.id} tool={t} onChanged={onChanged} />

      <div>
        <h4 className="mb-1 text-xs font-semibold tracking-wide text-text/60 uppercase">Lifecycle</h4>
        <ul className="flex flex-col gap-1 text-sm text-text/70">
          {t.lifecycle.map((e, i) => (
            <li key={i}>
              <span className="font-mono text-xs">{e.date}</span> — {e.state} ({e.by})
              {e.note && <span className="text-text/50"> · {e.note}</span>}
            </li>
          ))}
        </ul>
      </div>
    </Card>
  );
}
